/**
 * 세미나 등록·수정 폼 전용 타입과 순수 함수 모음.
 *
 * 서버 계약(docs/seminar-format-impl-plan.md 2절)의 요청 필드 이름을 그대로 쓴다.
 * 날짜·시각은 서버 LocalDateTime 과 같은 `YYYY-MM-DDTHH:mm` (로컬 시각, 시간대 없음) 문자열로 다룬다.
 */

/** 진행 방식 — 커리어 전용. 없으면 서버가 OFFLINE 으로 읽는다 */
export type SeminarFormatValue = 'OFFLINE' | 'LIVE' | 'VOD';
/** 일정 유형 — 커리어 전용. 동영상(VOD)은 PERIOD(기간) 또는 OPEN(상시 판매, 종료일 없음) */
export type SeminarScheduleValue = 'SINGLE' | 'PERIOD' | 'SESSIONS' | 'OPEN';
export type SeminarTypeValue = 'GENERAL' | 'CAREER';

/** 폼에서 편집하는 회차 한 줄 (날짜와 시각을 따로 받는다) */
export interface SessionInput {
  date: string; // YYYY-MM-DD
  startTime: string; // HH:mm
  endTime: string; // HH:mm
}

/** 등록/수정 요청의 회차 */
export interface SeminarSessionRequest {
  startDateTime: string;
  endDateTime: string;
  title?: string;
}

/** 최대 회차 수 (1단계) */
export const MAX_SESSIONS: number = 12;
/** 제목 최대 글자 수 */
export const TITLE_MAX_LENGTH: number = 40;
/** 접속 안내 최대 글자 수 */
export const ONLINE_GUIDE_MAX_LENGTH: number = 300;
/** 일반 세미나는 분야를 받지 않는다 — 서버 필수값이라 '기타' 1개로 채운다 */
export const GENERAL_DEFAULT_CATEGORY: string = 'ETC';
/** 일반 세미나 대상 레벨 기본값 */
export const GENERAL_DEFAULT_LEVEL: string = 'ANY';
/** 일반 세미나 정원 기본값 (신청을 받지 않아 의미 없음, 서버 최소 1) */
export const GENERAL_DEFAULT_CAPACITY: number = 10;

export const FORMAT_OPTIONS: Array<{ value: SeminarFormatValue; label: string }> = [
  { value: 'OFFLINE', label: '오프라인' },
  { value: 'LIVE', label: '라이브' },
  { value: 'VOD', label: '동영상' },
];

export const SCHEDULE_OPTIONS: Array<{ value: SeminarScheduleValue; label: string }> = [
  { value: 'SINGLE', label: '당일' },
  { value: 'PERIOD', label: '기간' },
  { value: 'SESSIONS', label: '회차' },
];

/** 동영상 일정 — 기간(시청 기간) / 상시(판매 시작일만, 종료일 없음) */
export const VOD_SCHEDULE_OPTIONS: Array<{ value: SeminarScheduleValue; label: string }> = [
  { value: 'PERIOD', label: '기간' },
  { value: 'OPEN', label: '상시' },
];

/** 상시 동영상 목록 일정 줄(서버 scheduleLabel 과 같은 문구) */
export const OPEN_SCHEDULE_LABEL: string = '상시 시청';

/** 상시 동영상 종료 자리값 — 서버 스키마 호환용(판매 시작 + 10년). 화면·정산·알림 어디서도 종료일로 쓰지 않는다 */
export const OPEN_END_PLACEHOLDER_YEARS: number = 10;

const WEEKDAYS: string[] = ['일', '월', '화', '수', '목', '금', '토'];

// 이모지(그림 문자·국기·결합 문자) — 제목에 넣으면 목록 카드 줄높이가 흔들려 입력 단계에서 뺀다
// (문자 묶음 [...] 안에 결합 문자를 넣으면 eslint no-misleading-character-class — 같은 범위를 유니코드 속성으로 나눠 쓴다)
const EMOJI_PATTERN: RegExp = /\p{Extended_Pictographic}|\p{Regional_Indicator}|\p{Emoji_Modifier}|\u200D|\uFE0F|\u20E3/gu;

/** 문자열에서 이모지를 모두 지운다. 이모지가 없으면 같은 문자열을 그대로 돌려준다(한글 조합 입력 보호). */
export const removeEmoji = (value: string): string => {
  EMOJI_PATTERN.lastIndex = 0;
  if (!EMOJI_PATTERN.test(value)) return value;
  EMOJI_PATTERN.lastIndex = 0;
  // 이모지가 빠진 자리에 남는 겹친 공백은 하나로
  return value.replace(EMOJI_PATTERN, '').replace(/ {2,}/g, ' ');
};

const pad2 = (n: number): string => String(n).padStart(2, '0');

/** Date → `YYYY-MM-DDTHH:mm` (로컬) */
export const formatDateTimeLocal = (date: Date): string =>
  `${date.getFullYear()}-${pad2(date.getMonth() + 1)}-${pad2(date.getDate())}T${pad2(date.getHours())}:${pad2(date.getMinutes())}`;

/** Date → `YYYY-MM-DD` (로컬) */
export const formatDateOnly = (date: Date): string =>
  `${date.getFullYear()}-${pad2(date.getMonth() + 1)}-${pad2(date.getDate())}`;

/** `YYYY-MM-DD` 또는 `YYYY-MM-DDTHH:mm[:ss]` → 로컬 Date. 형식이 틀리면 null */
export const parseLocal = (value?: string | null): Date | null => {
  if (!value) return null;
  const match = /^(\d{4})-(\d{2})-(\d{2})(?:T(\d{2}):(\d{2}))?/.exec(value);
  if (!match) return null;
  const [, y, m, d, hh, mm] = match;
  const date = new Date(Number(y), Number(m) - 1, Number(d), Number(hh ?? 0), Number(mm ?? 0));
  return Number.isNaN(date.getTime()) ? null : date;
};

/** `YYYY-MM-DD…` → `9/19(토)` */
export const formatMonthDay = (value: string): string => {
  const date = parseLocal(value);
  if (!date) return '';
  return `${date.getMonth() + 1}/${date.getDate()}(${WEEKDAYS[date.getDay()]})`;
};

/** 회차 한 줄 → 요청용 시작·종료 */
export const toSessionRequest = (session: SessionInput): SeminarSessionRequest => ({
  startDateTime: `${session.date}T${session.startTime}`,
  endDateTime: `${session.date}T${session.endTime}`,
});

/** 시작 시각 오름차순 정렬 (원본 배열은 건드리지 않는다) */
export const sortSessions = (sessions: SessionInput[]): SessionInput[] =>
  [...sessions].sort((a, b) => `${a.date}T${a.startTime}`.localeCompare(`${b.date}T${b.startTime}`));

/**
 * 새 회차가 들어갈 수 있는지 검사한다. 문제가 있으면 사람이 읽는 문구, 없으면 null.
 * - 날짜·시작·종료 모두 필요, 시작 < 종료, 시작은 지금 이후, 기존 회차와 시간이 겹치면 안 된다.
 */
export const validateSessionCandidate = (
  candidate: SessionInput,
  existing: SessionInput[],
  now: Date = new Date(),
): string | null => {
  if (!candidate.date || !candidate.startTime || !candidate.endTime) {
    return '날짜와 시작·종료 시각을 모두 입력해 주세요.';
  }
  const { startDateTime, endDateTime } = toSessionRequest(candidate);
  if (endDateTime <= startDateTime) {
    return '종료 시각은 시작 시각보다 늦어야 해요.';
  }
  const start = parseLocal(startDateTime);
  if (!start || start <= now) {
    return '이미 지난 시각이에요.\n지금 이후로 선택해 주세요.';
  }
  const overlapped = existing.find((s) => {
    const other = toSessionRequest(s);
    return startDateTime < other.endDateTime && other.startDateTime < endDateTime;
  });
  if (overlapped) {
    return `${formatMonthDay(overlapped.date)} ${overlapped.startTime} ~ ${overlapped.endTime} 회차와 시간이 겹쳐요.`;
  }
  return null;
};

/** 회차 목록 전체 검사 (정렬 후 겹침·순서·미래 시각). 문제가 없으면 null */
export const validateSessionList = (sessions: SessionInput[], now: Date = new Date()): string | null => {
  if (sessions.length === 0) return '회차를 1개 이상 추가해 주세요.';
  if (sessions.length > MAX_SESSIONS) return `회차는 최대 ${MAX_SESSIONS}회까지 추가할 수 있어요.`;
  const sorted = sortSessions(sessions);
  for (let i = 0; i < sorted.length; i += 1) {
    const message = validateSessionCandidate(sorted[i], sorted.slice(0, i), now);
    if (message) return `${i + 1}회차: ${message}`;
  }
  return null;
};

/**
 * 기간형 시작 시각. 수정 폼에서 날짜를 바꾸지 않았으면 원래 시각을 지켜 준다(기존 일반 세미나의 시각 보존).
 * 새로 고른 날짜면 그날 00:00.
 */
export const composePeriodStart = (date: string, original?: string | null): string =>
  original && original.slice(0, 10) === date ? original.slice(0, 16) : `${date}T00:00`;

/** 기간형 종료 시각. 날짜를 바꾸지 않았으면 원래 시각, 새 날짜면 그날 23:59 */
export const composePeriodEnd = (date: string, original?: string | null): string =>
  original && original.slice(0, 10) === date ? original.slice(0, 16) : `${date}T23:59`;

/**
 * 상시 동영상 판매 시작 시각. 수정 폼에서 날짜를 그대로 두면 원래 시각, 오늘이면 다음 분(지금 바로 공개), 그 밖의 날은 00:00.
 */
export const composeOpenSaleStart = (date: string, original?: string | null, now: Date = new Date()): string => {
  if (original && original.slice(0, 10) === date) return original.slice(0, 16);
  if (date === formatDateOnly(now)) {
    const next: Date = new Date(now.getTime() + 60 * 1000);
    return formatDateTimeLocal(next);
  }
  return `${date}T00:00`;
};

/** 상시 동영상 종료 자리값 = 판매 시작 + 10년 (`YYYY-MM-DDTHH:mm`) */
export const openEndPlaceholder = (start: string): string => {
  const date: Date | null = parseLocal(start);
  if (!date) return start;
  date.setFullYear(date.getFullYear() + OPEN_END_PLACEHOLDER_YEARS);
  return formatDateTimeLocal(date);
};

interface ScheduleLabelInput {
  format: SeminarFormatValue;
  schedule: SeminarScheduleValue;
  singleStart?: string;
  singleEnd?: string;
  periodStartDate?: string;
  periodEndDate?: string;
  sessions?: SessionInput[];
}

/**
 * 목록 카드 일정 줄 미리보기 — 서버 scheduleLabel 과 같은 규칙.
 * `10/15(목)` · `10/13(화) ~ 11/3(화)` · `9/19(토) ~ 10/10(토) · 4회` · `10/12(월) ~ 10/25(일) 시청`
 * 입력이 모자라면 null.
 */
export const buildScheduleLabel = (input: ScheduleLabelInput): string | null => {
  if (input.format === 'VOD' && input.schedule === 'OPEN') return OPEN_SCHEDULE_LABEL;
  const schedule: SeminarScheduleValue = input.format === 'VOD' ? 'PERIOD' : input.schedule;

  if (schedule === 'SESSIONS') {
    const sorted = sortSessions(input.sessions ?? []);
    if (sorted.length === 0) return null;
    const first = formatMonthDay(sorted[0].date);
    const last = formatMonthDay(sorted[sorted.length - 1].date);
    const range = sorted[0].date === sorted[sorted.length - 1].date ? first : `${first} ~ ${last}`;
    return `${range} · ${sorted.length}회`;
  }

  if (schedule === 'PERIOD') {
    if (!input.periodStartDate || !input.periodEndDate) return null;
    const start = formatMonthDay(input.periodStartDate);
    const end = formatMonthDay(input.periodEndDate);
    const range = input.periodStartDate === input.periodEndDate ? start : `${start} ~ ${end}`;
    return input.format === 'VOD' ? `${range} 시청` : range;
  }

  if (!input.singleStart) return null;
  const start = formatMonthDay(input.singleStart);
  if (input.singleEnd && input.singleEnd.slice(0, 10) !== input.singleStart.slice(0, 10)) {
    return `${start} ~ ${formatMonthDay(input.singleEnd)}`;
  }
  return start;
};

/**
 * 접속 링크는 링크 모양이기만 하면 된다(2026-09-29 사용자) — 줌 앱에서 복사하면 "zoom.us/j/123…"처럼 https:// 가 빠질 수 있다.
 * 앞에 http(s):// 는 있어도 없어도 되고, 점이 들어간 주소에 공백이 없어야 한다. 저장할 때는 normalizeOnlineUrl 로 https:// 를 붙인다.
 */
export const isValidOnlineUrl = (value?: string | null): boolean =>
  !!value && /^(https?:\/\/)?[^\s/.]+(\.[^\s/.]+)+(\/\S*)?$/i.test(value.trim());

/** 앞에 http(s):// 가 없으면 https:// 를 붙인다(서버 Seminar.normalizeOnlineAccess 와 같은 규칙) */
export const normalizeOnlineUrl = (value: string): string => {
  const url: string = value.trim();
  return /^https?:\/\//i.test(url) ? url : `https://${url}`;
};

// ─── 폐강 가능 시점 (서버 SeminarRepositoryImpl.tryCancelSeminarAtomic 과 같은 규칙) ─────────────

/** 폐강 판정에 필요한 세미나 필드 (상세 응답 그대로 넘겨도 된다) */
export interface CancelWindowSource {
  seminarType?: string;
  format?: string;
  schedule?: string;
  startDateTime?: string;
  endDateTime?: string;
  isCancelled?: boolean;
}

/** 여러 날 진행되는 커리어(기간·회차, 동영상 포함) — 진행 중에도 종료 전까지 폐강할 수 있다 */
export const isMultiDayCareer = (seminar: CancelWindowSource): boolean =>
  seminar.seminarType === 'CAREER' &&
  (seminar.format === 'VOD' || seminar.schedule === 'PERIOD' || seminar.schedule === 'SESSIONS');

/** 시작했고 아직 끝나지 않았는지 */
export const isSeminarInProgress = (seminar: CancelWindowSource, now: Date = new Date()): boolean => {
  const start = parseLocal(seminar.startDateTime);
  const end = parseLocal(seminar.endDateTime);
  return !!start && start <= now && (!end || end > now);
};

/**
 * 지금 폐강할 수 있는지 — 당일(및 일반)은 시작 전까지, 커리어 기간·회차·동영상은 종료 전까지.
 */
export const canCancelSeminar = (seminar: CancelWindowSource, now: Date = new Date()): boolean => {
  if (seminar.isCancelled) return false;
  // 상시 동영상은 종료일이 없다(endDateTime 은 자리값) — 판매 중이든 판매 종료든 폐강할 수 있다(이미 결제한 분 전원 전액 환불)
  if (seminar.format === 'VOD' && seminar.schedule === 'OPEN') return true;
  const start = parseLocal(seminar.startDateTime);
  if (!start || start > now) return true;
  if (!isMultiDayCareer(seminar)) return false;
  const end = parseLocal(seminar.endDateTime);
  return !!end && end > now;
};

/** 진행 중 폐강 확인 문구 (폐강 모달에 한 줄 추가) — 기간·회차형 */
export const MIDWAY_CANCEL_NOTICE: string = '진행 중 폐강은 전 회차 전액 환불되며 정산에서 차감됩니다.';

/** 상시 동영상(OPEN) 폐강 고지 — 회차가 없으니 "이미 결제한 분 전원" */
export const OPEN_CANCEL_NOTICE: string = '판매 중 폐강하면 이미 결제한 분 전원 전액 환불되며 정산에서 차감됩니다.';

/** 폐강 고지 한 줄 — 상시 동영상이면 판매 중 문구, 그 밖은 진행 중(전 회차) 문구 */
export const midwayCancelNoticeOf = (seminar: CancelWindowSource): string =>
  seminar.format === 'VOD' && seminar.schedule === 'OPEN' ? OPEN_CANCEL_NOTICE : MIDWAY_CANCEL_NOTICE;
